import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Loader, Modal } from 'rsuite';
import NavigationBar from '../Shared/NavigationBar';
import { useAuth } from '../Auth/AuthContext';
import { MOVE_TARGET_LABELS, RESULT_MESSAGES, TIERS } from '../../../shared/playtest';
import CardDetailPanel from './CardDetailPanel';
import PlaytestBoard from './PlaytestBoard';
import PlaytestControls from './PlaytestControls';
import PlaytestHand from './PlaytestHand';
import PlaytestPiles from './PlaytestPiles';
import { displayOf } from './playtestDisplay';
import { COMMAND_TO, createDragStore, PlaytestDragProvider } from './PlaytestDnD';
import ReplacementPrompt from './ReplacementPrompt';
import TierSelector from './TierSelector';
import { usePlaytestSessions } from './PlaytestSessionProvider';

const SPACING = { section: 16, row: 8, refusalPadY: 4, refusalPadX: 12 };
const COLORS = { refusalBg: 'rgba(0, 0, 0, 0.85)', refusalText: '#ffffff' };
const Z_INDEX = { refusal: 1000 };
const REFUSAL_LEFT = '50%';

const styles = {
  page: { display: 'flex', flexDirection: 'column', gap: SPACING.section, padding: SPACING.section },
  row: { display: 'flex', flexWrap: 'wrap', gap: SPACING.row, alignItems: 'center' },
  refusal: {
    position: 'fixed',
    left: REFUSAL_LEFT,
    bottom: SPACING.section,
    transform: 'translateX(-50%)',
    pointerEvents: 'none',
    zIndex: Z_INDEX.refusal,
    padding: `${SPACING.refusalPadY}px ${SPACING.refusalPadX}px`,
    background: COLORS.refusalBg,
    color: COLORS.refusalText,
  },
};

const tierLabel = (tierId) => TIERS.find((tier) => tier.id === tierId)?.label ?? tierId;

/**
 * Drop handling for the board, piles, and hand. The engine decides every drop
 * through the page's drag store, so a hover and its drop share one decision: an
 * action is dispatched, a prompt waits for a choice, and a rejected drop does
 * nothing.
 */
const dropHandlers = (state, dragStore, { onAction, onPrompt, onRefuse }) => ({
  canDrop: (target) => {
    const outcome = dragStore.decide(state, target);
    return outcome !== null && outcome.kind !== 'reject';
  },
  onRefuse: (target) => {
    const outcome = dragStore.decide(state, target);
    if (outcome?.kind === 'reject') onRefuse(RESULT_MESSAGES[outcome.reason]);
  },
  onDrop: (target) => {
    const outcome = dragStore.decide(state, target);
    dragStore.end();
    if (outcome?.kind === 'action') onAction(outcome.action);
    if (outcome?.kind === 'prompt') onPrompt({ instanceId: outcome.instanceId, to: outcome.to });
  },
});

/**
 * Playtest screen for one deck. The whole deck loads before play; the tier is
 * chosen once per session and reused on re-entry. Changing the tier later does
 * not touch the board. One card detail panel serves the board, piles, and hand.
 */
const PlaytestDeck = ({ collectionId }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { sessions, startDeckLoad, setTier, endSession, dispatchAction } = usePlaytestSessions();
  const session = sessions.get(collectionId);
  const [changingTier, setChangingTier] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [dropPrompt, setDropPrompt] = useState(null);
  const [refusal, setRefusal] = useState(null);
  const [dragStore] = useState(createDragStore);

  useEffect(() => {
    if (!session && isAuthenticated) startDeckLoad(collectionId);
  }, [collectionId, session, isAuthenticated, startDeckLoad]);

  // A drop prompt belongs to the session it was opened on. Sign-out or a change of user removes
  // that session, so the prompt must not outlive it.
  useEffect(() => {
    if (!session) setDropPrompt(null);
  }, [session]);

  // The refusal line lasts for one hover; a drag that ends clears it.
  useEffect(() => {
    const clearRefusal = () => setRefusal(null);
    window.addEventListener('dragend', clearRefusal);
    return () => window.removeEventListener('dragend', clearRefusal);
  }, []);

  const chooseTier = (tier) => {
    setTier(collectionId, tier);
    setChangingTier(false);
  };

  const endPlaytest = () => {
    endSession(collectionId);
    navigate(`/library/${collectionId}`);
  };

  const chooseDropDestination = (to) => {
    dispatchAction(collectionId, { type: 'move', instanceId: dropPrompt.instanceId, to });
    setDropPrompt(null);
  };

  const renderPrompt = () => {
    const name = displayOf(
      session,
      session.engine.instances[dropPrompt.instanceId].cardId
    ).name;
    return (
      <Modal open onClose={() => setDropPrompt(null)} size="xs">
        <Modal.Header>
          <Modal.Title>Command-zone card</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ReplacementPrompt
            name={name}
            destinationLabel={MOVE_TARGET_LABELS[dropPrompt.to]}
            onDestination={() => chooseDropDestination(dropPrompt.to)}
            onCommand={() => chooseDropDestination(COMMAND_TO)}
            onCancel={() => setDropPrompt(null)}
          />
        </Modal.Body>
      </Modal>
    );
  };

  const renderBody = () => {
    if (!session || session.status === 'loading') {
      return (
        <div data-testid="playtest-loading">
          <Loader center content="Loading deck…" />
        </div>
      );
    }
    if (session.status === 'error') {
      return (
        <div data-testid="playtest-load-error" style={styles.row}>
          <span>{session.error}</span>
          <Button data-testid="playtest-retry-deck" onClick={() => startDeckLoad(collectionId)}>
            Retry
          </Button>
        </div>
      );
    }
    if (session.tier === null) {
      return <TierSelector onChoose={chooseTier} />;
    }
    const drops = dropHandlers(session.engine, dragStore, {
      onAction: (action) => dispatchAction(collectionId, action),
      onPrompt: setDropPrompt,
      onRefuse: setRefusal,
    });
    return (
      <>
        <div style={styles.row}>
          <span data-testid="playtest-tier-label">{tierLabel(session.tier)}</span>
          <Button data-testid="playtest-change-tier" onClick={() => setChangingTier(true)}>
            Change tier
          </Button>
        </div>
        {changingTier ? <TierSelector onChoose={chooseTier} /> : null}
        {refusal ? <span data-testid="drop-refusal" role="status" style={styles.refusal}>{refusal}</span> : null}
        <PlaytestControls
          collectionId={collectionId}
          session={session}
          onEndSession={endPlaytest}
        />
        <PlaytestBoard
          collectionId={collectionId}
          session={session}
          onOpenDetail={setDetailId}
          drops={drops}
        />
        <PlaytestPiles collectionId={collectionId} session={session} drops={drops} />
        <PlaytestHand
          collectionId={collectionId}
          session={session}
          onOpenDetail={setDetailId}
          drops={drops}
        />
        {detailId !== null ? (
          <CardDetailPanel
            collectionId={collectionId}
            session={session}
            instanceId={detailId}
            onClose={() => setDetailId(null)}
          />
        ) : null}
        {dropPrompt !== null ? renderPrompt() : null}
      </>
    );
  };

  return (
    <PlaytestDragProvider store={dragStore}>
      <NavigationBar />
      <div style={styles.page}>
        <div style={styles.row}>
          <Button data-testid="playtest-back" onClick={() => navigate(`/library/${collectionId}`)}>
            Back to deck
          </Button>
        </div>
        {renderBody()}
      </div>
    </PlaytestDragProvider>
  );
};

/** Keyed by deck id, so switching decks starts from fresh local state. */
const PlaytestPage = () => {
  const { id } = useParams();
  return <PlaytestDeck key={id} collectionId={id} />;
};

export default PlaytestPage;
